import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { Paperclip } from "lucide-react";
import {
  dataTransferHasFiles,
  filesFromDataTransfer,
} from "@/lib/compose/composeAttachments";
import { cn } from "@/lib/cn";

type Props = {
  disabled?: boolean;
  onAddFiles: (files: File[]) => void | Promise<void>;
  className?: string;
  children: ReactNode;
};

/**
 * Zone drag & drop fichiers → pièces jointes (rédaction / réponse / transfert).
 */
export function ComposeFileDropZone({
  disabled = false,
  onAddFiles,
  className,
  children,
}: Props) {
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const reset = useCallback(() => {
    setDragging(false);
  }, []);

  // Filet : drop dans l’éditeur (stopPropagation) ou sortie fenêtre → masquer l’overlay.
  useEffect(() => {
    if (!dragging) return;
    const clear = () => reset();
    window.addEventListener("drop", clear, true);
    window.addEventListener("dragend", clear, true);
    return () => {
      window.removeEventListener("drop", clear, true);
      window.removeEventListener("dragend", clear, true);
    };
  }, [dragging, reset]);

  const onDragEnter = useCallback(
    (event: DragEvent) => {
      if (disabled || !dataTransferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      setDragging(true);
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (event: DragEvent) => {
      const related = event.relatedTarget as Node | null;
      if (related && rootRef.current?.contains(related)) return;
      reset();
    },
    [reset]
  );

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (disabled || !dataTransferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
    },
    [disabled]
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (disabled) return;
      if (!dataTransferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      reset();
      const files = filesFromDataTransfer(event.dataTransfer);
      if (files.length > 0) void onAddFiles(files);
    },
    [disabled, onAddFiles, reset]
  );

  return (
    <div
      ref={rootRef}
      className={cn("relative", className)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {children}
      {dragging ? (
        <div
          className="compose-file-drop-overlay pointer-events-none absolute inset-0 z-40 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-[var(--mail-accent)] bg-[var(--mail-accent-soft)]/80 backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-2 px-4 text-center">
            <Paperclip className="h-6 w-6 text-[var(--mail-accent)]" aria-hidden />
            <p className="text-sm font-semibold text-[var(--mail-text)]">
              Déposer pour joindre
            </p>
            <p className="text-xs text-[var(--mail-muted)]">
              Les fichiers seront ajoutés en pièces jointes
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
